import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const FeaturedProductsSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const featuredProducts = [
    {
      name: "Turmeric",
      image: "/images/products/turmeric.webp",
      description: "Organic turmeric with high curcumin content — fresh, finger, flakes & powder",
      tag: "Organic"
    },
    {
      name: "Ginger",
      image: "/images/products/ginger.webp",
      description: "Premium organic ginger — fresh, flakes & powder from certified farms",
      tag: "Organic"
    },
    {
      name: "Buckwheat",
      image: "/images/products/buckwheat.webp",
      description: "Nutritious organic buckwheat — unhulled & flour, gluten-free and rich in protein",
      tag: "Organic"
    },
    {
      name: "Large Black Cardamom",
      image: "/images/products/black-cardamom.webp",
      description: "Bold, smoky large black cardamom with intense aroma, naturally dried",
      tag: "Organic"
    },
    {
      name: "Coffee Beans",
      image: "/images/products/coffee.webp",
      description: "Organic Robusta & Arabica coffee beans, available green and roasted",
      tag: "Organic"
    },
    {
      name: "Cocoa",
      image: "/images/products/cocoa.webp",
      description: "Premium organic cocoa — fresh pods & naturally fermented dried beans",
      tag: "Organic"
    },
    {
      name: "Nutmeg",
      image: "/images/products/nutmeg.webp",
      description: "Aromatic organic nutmeg — carefully dried & shelled",
      tag: "Organic"
    },
    {
      name: "Cardamom",
      image: "/images/products/green-cardamom.webp",
      description: "Fragrant organic green cardamom pods, hand-picked at peak maturity",
      tag: "Organic"
    },
    {
      name: "Black Pepper",
      image: "/images/products/black-pepper.webp",
      description: "Bold organic black peppercorns with high piperine content",
      tag: "Organic"
    },
    {
      name: "Fresh Semi Husk Coconuts",
      image: "https://images.unsplash.com/photo-1553787434-dd9eb4ea4d0b?auto=format&fit=crop&w=1200&q=75",
      description: "Premium fresh semi-husked coconuts for export",
      tag: "Organic"
    },
    {
      name: "Pomegranates",
      image: "https://images.unsplash.com/photo-1574709755254-fcd942d09d5a?auto=format&fit=crop&w=1200&q=75",
      description: "Premium quality pomegranates rich in antioxidants",
      tag: "Export Quality"
    },
    {
      name: "Grapes",
      image: "https://images.unsplash.com/photo-1596363505729-4190a9506133?auto=format&fit=crop&w=1200&q=75",
      description: "Fresh, juicy grapes directly from farms",
      tag: "Export Quality"
    },
    {
      name: "Bananas",
      image: "https://images.unsplash.com/photo-1640958900081-7b069dd23e9c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxiYW5hbmFzfGVufDB8fHx8MTc3Mzg2NDQxNHww&ixlib=rb-4.1.0&q=85",
      description: "Export-quality ripe bananas with rich flavor",
      tag: "Export Quality"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredProducts.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [featuredProducts.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredProducts.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + featuredProducts.length) % featuredProducts.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <section className="py-16 bg-gradient-to-b from-white to-amber-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-orange-500 font-semibold text-sm uppercase tracking-wider">Featured Products</span>
          <h2 className="text-4xl md:text-5xl font-bold text-amber-950 mt-3 mb-4">
            Our Premium Exports
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Discover our selection of high-quality agricultural products sourced directly from certified farms
          </p>
        </div>

        {/* Slider Container */}
        <div className="relative">
          <div className="relative overflow-hidden rounded-3xl shadow-2xl bg-white">
            {/* Slides */}
            <div className="relative h-[500px]">
              {featuredProducts.map((product, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                    index === currentSlide
                      ? 'opacity-100 translate-x-0'
                      : index < currentSlide
                      ? 'opacity-0 -translate-x-full'
                      : 'opacity-0 translate-x-full'
                  }`}
                >
                  <div className="grid md:grid-cols-2 h-full">
                    {/* Image Side */}
                    <div className="relative h-full overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        loading={index === 0 ? 'eager' : 'lazy'}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
                    </div>

                    {/* Content Side */}
                    <div className="flex flex-col justify-center p-12 md:p-16 bg-gradient-to-br from-green-50 to-amber-50">
                      <div className="inline-block mb-4">
                        <span className="bg-green-700 text-white px-4 py-2 rounded-full text-sm font-semibold">
                          {product.tag}
                        </span>
                      </div>
                      <h3 className="text-5xl font-bold text-amber-950 mb-6">
                        {product.name}
                      </h3>
                      <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                        {product.description}
                      </p>
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => {
                            const element = document.querySelector('#contact');
                            if (element) element.scrollIntoView({ behavior: 'smooth' });
                          }}
                          className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                        >
                          Request Quote
                        </button>
                        <button
                          onClick={() => {
                            const element = document.querySelector('#products');
                            if (element) element.scrollIntoView({ behavior: 'smooth' });
                          }}
                          className="text-green-700 font-semibold hover:text-green-800 transition-colors text-lg"
                        >
                          View All Products →
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-xl transition-all hover:scale-110 z-10"
            >
              <ChevronLeft size={28} />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-xl transition-all hover:scale-110 z-10"
            >
              <ChevronRight size={28} />
            </button>
          </div>

          {/* Slide Indicators */}
          <div className="flex justify-center mt-8 space-x-3">
            {featuredProducts.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentSlide
                    ? 'bg-green-700 w-12 h-3'
                    : 'bg-gray-300 hover:bg-gray-400 w-3 h-3'
                }`}
              />
            ))}
          </div>

          {/* Product Names */}
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {featuredProducts.map((product, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                  index === currentSlide
                    ? 'bg-green-700 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
                }`}
              >
                {product.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProductsSlider;
